import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from '@/features/auth';

// Plan types
export type CompanyPlan = 'BASIC' | 'PRO' | null;

// View mode storage key
const VIEW_MODE_KEY = 'facturas_view_mode';

interface CompanyPlanContextType {
  plan: CompanyPlan;
  isBasicPlan: boolean;
  isProPlan: boolean;
  isLoading: boolean;
  // View mode derived from plan
  viewMode: 'basic' | 'pro';
  setViewModeOverride: (mode: 'basic' | 'pro' | null) => void;
}

const CompanyPlanContext = createContext<CompanyPlanContextType | undefined>(undefined);

interface CompanyPlanProviderProps {
  children: ReactNode;
}

export const CompanyPlanProvider: React.FC<CompanyPlanProviderProps> = ({ children }) => {
  const { company, isLoggedIn } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [viewModeOverride, setViewModeOverride] = useState<'basic' | 'pro' | null>(null);
  
  // Derive plan from company data
  const plan: CompanyPlan = company?.plan?.toUpperCase() as CompanyPlan || null;
  const isBasicPlan = plan === 'BASIC';
  const isProPlan = plan === 'PRO';
  
  // View mode: use override if set, otherwise derive from plan
  // BASIC plan forces basic mode, PRO plan defaults to pro but allows switching
  const viewMode: 'basic' | 'pro' = isBasicPlan 
    ? 'basic' 
    : (viewModeOverride || 'pro');
  
  // Update localStorage when view mode changes
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
    // Dispatch event so other components can react
    window.dispatchEvent(new CustomEvent('viewModeChange', { detail: viewMode }));
  }, [viewMode]);
  
  // Track loading state
  useEffect(() => {
    if (isLoggedIn && company !== null) {
      setIsLoading(false);
    } else if (!isLoggedIn) {
      setIsLoading(false);
    }
  }, [isLoggedIn, company]);
  
  // Log plan for debugging
  useEffect(() => {
    console.log('📋 CompanyPlanContext - Plan detected:', { 
      plan, 
      isBasicPlan, 
      isProPlan, 
      viewMode,
      companyPlanRaw: company?.plan 
    });
  }, [plan, isBasicPlan, isProPlan, viewMode, company?.plan]);
  
  const handleSetViewModeOverride = useCallback((mode: 'basic' | 'pro' | null) => {
    // Only allow override for PRO plans
    if (!isBasicPlan) {
      setViewModeOverride(mode);
    }
  }, [isBasicPlan]);

  const value: CompanyPlanContextType = {
    plan,
    isBasicPlan,
    isProPlan,
    isLoading,
    viewMode,
    setViewModeOverride: handleSetViewModeOverride,
  };

  return (
    <CompanyPlanContext.Provider value={value}>
      {children}
    </CompanyPlanContext.Provider>
  );
};

export const useCompanyPlan = (): CompanyPlanContextType => {
  const context = useContext(CompanyPlanContext);
  if (context === undefined) {
    throw new Error('useCompanyPlan must be used within a CompanyPlanProvider');
  }
  return context;
};

export default CompanyPlanContext;
